/**
 * *Pridať hodnotenie* (PRD-0075 R-E2): a verdict, a confidence, the rationale and what it rests
 * on. An assessment is never edited once added: a correction is a new one. The feature submits;
 * a refusal comes back as its message.
 */
import { X } from 'lucide-react'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  type EvidenceRefIn,
  HYPOTHESIS_CONFIDENCES,
  HYPOTHESIS_VERDICTS,
  type HypothesisAssessmentCreate,
  type HypothesisConfidence,
  type HypothesisVerdict,
} from '@/types/analytic-hypothesis'
import { cn } from '@/utils/cn'
import { type EvidenceOption, EvidencePicker, type EvidenceSource } from './EvidencePicker'
import { VERDICT_FILL } from './strip'
import { useSubmission } from './use-submission'

const COPY = 'analyticHypotheses'

export interface AddAssessmentFormProps {
  /** The sources the evidence pickers offer, in order. */
  sources: readonly EvidenceSource[]
  /** Resolves to null once added, or to the message of why not. */
  onSubmit: (body: HypothesisAssessmentCreate) => Promise<string | null>
}

const sameRef = (a: EvidenceRefIn, b: EvidenceRefIn) => a.kind === b.kind && a.id === b.id

function labelOf(sources: readonly EvidenceSource[], ref: EvidenceRefIn): string {
  const items: readonly EvidenceOption[] =
    sources.find(source => source.kind === ref.kind)?.items ?? []
  return items.find(item => item.id === ref.id)?.label ?? ref.id
}

function Picked({
  sources,
  picked,
  onRemove,
}: {
  sources: readonly EvidenceSource[]
  picked: readonly EvidenceRefIn[]
  onRemove: (ref: EvidenceRefIn) => void
}) {
  const { t } = useTranslation()
  if (picked.length === 0) return null
  return (
    <ul className="flex flex-wrap gap-1">
      {picked.map(ref => {
        const name = labelOf(sources, ref)
        return (
          <li key={`${ref.kind}:${ref.id}`}>
            <Badge variant="outline" className="h-6 max-w-64 pr-1">
              <span className="text-muted-foreground">
                {t(`${COPY}.assessment.kinds.${ref.kind}`)}
              </span>
              <span className="truncate">{name}</span>
              <button
                type="button"
                aria-label={t(`${COPY}.form.removeEvidence`, { name })}
                onClick={() => onRemove(ref)}
                className="rounded-sm text-muted-foreground hover:text-foreground"
              >
                <X aria-hidden className="size-3" />
              </button>
            </Badge>
          </li>
        )
      })}
    </ul>
  )
}

interface ChoiceOption<T extends string> {
  value: T
  content: ReactNode
}

function Choice<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly ChoiceOption<T>[]
  value: T | ''
  onChange: (value: T | '') => void
}) {
  const labelId = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="text-sm font-medium">
        {label}
      </span>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        aria-labelledby={labelId}
        value={value}
        onValueChange={next => onChange(next as T | '')}
      >
        {options.map(option => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className="data-[state=on]:border-primary/50 data-[state=on]:bg-primary/15 data-[state=on]:text-foreground"
          >
            {option.content}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

/** The form's fields, and the request body once every required one is filled. */
function useAssessmentDraft() {
  const [verdict, setVerdict] = useState<HypothesisVerdict | ''>('')
  const [confidence, setConfidence] = useState<HypothesisConfidence | ''>('')
  const [rationale, setRationale] = useState('')
  const [evidence, setEvidence] = useState<EvidenceRefIn[]>([])
  const toggle = (ref: EvidenceRefIn) =>
    setEvidence(current =>
      current.some(other => sameRef(other, ref))
        ? current.filter(other => !sameRef(other, ref))
        : [...current, ref]
    )
  const reset = () => {
    setVerdict('')
    setConfidence('')
    setRationale('')
    setEvidence([])
  }
  const body: HypothesisAssessmentCreate | null =
    verdict !== '' && confidence !== '' && rationale.trim().length > 0
      ? { verdict, confidence, rationale: rationale.trim(), evidence }
      : null
  return {
    fields: { verdict, setVerdict, confidence, setConfidence, rationale, setRationale },
    evidence,
    toggle,
    reset,
    body,
  }
}

function Judgment({ fields }: { fields: ReturnType<typeof useAssessmentDraft>['fields'] }) {
  const { t } = useTranslation()
  const verdicts = HYPOTHESIS_VERDICTS.map(value => ({
    value,
    content: (
      <>
        <span aria-hidden className={cn('size-2 rounded-sm', VERDICT_FILL[value])} />
        {t(`${COPY}.verdict.${value}`)}
      </>
    ),
  }))
  const confidences = HYPOTHESIS_CONFIDENCES.map(value => ({
    value,
    content: t(`${COPY}.confidenceLevel.${value}`),
  }))
  return (
    <div className="flex flex-wrap gap-4">
      <Choice<HypothesisVerdict>
        label={t(`${COPY}.form.verdict`)}
        options={verdicts}
        value={fields.verdict}
        onChange={fields.setVerdict}
      />
      <Choice<HypothesisConfidence>
        label={t(`${COPY}.form.confidence`)}
        options={confidences}
        value={fields.confidence}
        onChange={fields.setConfidence}
      />
    </div>
  )
}

function EvidenceField({
  sources,
  picked,
  onToggle,
}: {
  sources: readonly EvidenceSource[]
  picked: readonly EvidenceRefIn[]
  onToggle: (ref: EvidenceRefIn) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{t(`${COPY}.form.evidence`)}</span>
      <div className="flex flex-wrap gap-2">
        {sources.map(source => (
          <EvidencePicker key={source.kind} source={source} picked={picked} onToggle={onToggle} />
        ))}
      </div>
      <Picked sources={sources} picked={picked} onRemove={onToggle} />
    </div>
  )
}

export function AddAssessmentForm({ sources, onSubmit }: AddAssessmentFormProps) {
  const { t } = useTranslation()
  const rationaleId = useId()
  const { fields, evidence, toggle, reset, body } = useAssessmentDraft()
  const submission = useSubmission(t(`${COPY}.form.failed`))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!body || submission.pending) return
    if (await submission.run(() => onSubmit(body))) reset()
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4"
    >
      <h3 className="text-sm font-semibold text-foreground">{t(`${COPY}.form.title`)}</h3>
      <Judgment fields={fields} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={rationaleId}>{t(`${COPY}.form.rationale`)}</Label>
        <Textarea
          id={rationaleId}
          rows={4}
          value={fields.rationale}
          onChange={event => fields.setRationale(event.target.value)}
          placeholder={t(`${COPY}.form.rationalePlaceholder`)}
        />
      </div>
      <EvidenceField sources={sources} picked={evidence} onToggle={toggle} />
      {submission.error && (
        <p role="alert" className="text-xs text-destructive">
          {submission.error}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-2xs text-muted-foreground">{t(`${COPY}.form.immutable`)}</p>
        <Button type="submit" size="sm" disabled={!body || submission.pending}>
          {submission.pending ? t(`${COPY}.form.submitting`) : t(`${COPY}.form.submit`)}
        </Button>
      </div>
    </form>
  )
}
